import { User as FirebaseUser } from 'firebase/auth';

export type JWTRole = { name?: string; [key: string]: unknown };

export type JWTEnvironment = {
  id?: string;
  deploymentLocation?: string;
  deploymentKey?: string;
};

export type JWTOrganization = {
  id?: string;
  name?: string;
  environments?: JWTEnvironment[];
} & Record<string, unknown>;

export type JWTPayload = {
  id?: string;
  organization?: JWTOrganization;
  roles?: JWTRole[];
} & Record<string, unknown>;

export const getPayloadJWT = (str: string): JWTPayload => {
  try {
    if (!str) throw new Error('Invalid token');
    const parts = str.split('.');
    if (parts.length !== 3) throw new Error('Invalid token format');

    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const decoded = atob(paddedPayload);

    return JSON.parse(decoded) as JWTPayload;
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : String(e);
    throw new Error('Failed to decode JWT: ' + message);
  }
};

export const isJWT = (str: unknown): str is string => {
  if (!str || typeof str !== 'string') return false;

  const parts = str.split('.');
  if (parts.length !== 3) return false;

  try {
    for (const part of parts) {
      const cleaned = part.replace(/-/g, '+').replace(/_/g, '/');
      const padded = cleaned + '='.repeat((4 - (cleaned.length % 4)) % 4);
      atob(padded);
    }
    return true;
  } catch {
    return false;
  }
};

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const extractUserIdFromToken = (token: string): string | undefined => {
  try {
    const decoded = getPayloadJWT(token);
    return decoded?.id;
  } catch {
    return undefined;
  }
};

// Waits until the current user's token custom claims include the given orgId
export const waitForOrganizationClaim = async ({
  user,
  targetOrgId,
  maxAttempts,
  delayMs,
}: {
  user: FirebaseUser;
  targetOrgId: string;
  maxAttempts: number;
  delayMs: number;
}): Promise<boolean> => {
  for (let i = 0; i < maxAttempts; i++) {
    await user.getIdToken(true);
    const refreshedToken = await user.getIdToken();
    const refreshedDecoded = refreshedToken
      ? getPayloadJWT(refreshedToken)
      : null;
    if (refreshedDecoded?.organization?.id === targetOrgId) {
      return true;
    }
    await sleep(delayMs);
  }
  return false;
};
